/**
 * Partner Overview cards. TMMPDM-0088: At a glance is read-only, so the Rate card tile and the
 * Standing card's sellable note are gone (Needs you carries what needs acting on); Latest
 * settlement, Subscribers and Affiliates stay, each opening its report.
 */
import { Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type { FleetDashboard, ResellerDto } from '@/api/tmm/types';
import {
  StandingSummaryCard,
  LatestSettlementCard,
  AffiliatesSummaryCard,
  OverviewCardGrid,
  CardShell,
  CardFrame,
  LoadingCard,
  FailedCard,
  useRead,
} from '../resellers/ResellerOverviewCards';
import { periodFor } from '../dashboard/period';

function SubscribersCard({
  reseller,
  tenantDashboard,
}: {
  reseller: ResellerDto;
  tenantDashboard: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
}) {
  const { t } = useCopy();
  const title = t('partners.group.subscribers', 'Subscribers');
  const read = useRead(reseller.id, async () => {
    if (!reseller.tenant) return null;
    const period = periodFor('THIS_MONTH');
    return tenantDashboard(reseller.tenant.slug, { from: period.from, to: period.to });
  });
  if (!reseller.tenant)
    return (
      <CardFrame to="?tab=subscriptions" title={title}>
        {t('partners.noTenant', 'No tenant yet')}
      </CardFrame>
    );
  if (read.failure)
    return <FailedCard title={title} failure={read.failure} onRetry={read.reload} />;
  if (read.loading) return <LoadingCard title={title} />;
  return (
    <CardFrame to="?tab=subscriptions" title={title}>
      <Typography>{read.data?.tenants[0]?.subscribers.active ?? '—'}</Typography>
    </CardFrame>
  );
}

export function PartnerOverviewCards({
  reseller,
  standing,
  tenantDashboard,
}: {
  reseller: ResellerDto;
  standing: Parameters<typeof StandingSummaryCard>[0];
  tenantDashboard: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
}) {
  return (
    <OverviewCardGrid>
      <StandingSummaryCard {...standing} />
      <LatestSettlementCard resellerId={reseller.id} />
      <CardShell>
        <SubscribersCard
          key={reseller.tenant?.slug ?? 'no-tenant'}
          reseller={reseller}
          tenantDashboard={tenantDashboard}
        />
      </CardShell>
      <AffiliatesSummaryCard resellerId={reseller.id} />
    </OverviewCardGrid>
  );
}
